import { supabase } from '../lib/supabase';

export const goalService = {
  // Get all goals for a user
  async getGoals(userId, statusFilter = null) {
    let query = supabase?.from('financial_goals')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (statusFilter) {
      query = query?.eq('status', statusFilter);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(goal => ({
      id: goal?.id,
      userId: goal?.user_id,
      goalName: goal?.goal_name,
      goalType: goal?.goal_type,
      targetAmount: parseFloat(goal?.target_amount),
      currentAmount: parseFloat(goal?.current_amount),
      deadline: goal?.deadline,
      status: goal?.status,
      linkedAccountId: goal?.linked_account_id,
      autoCloseOnCompletion: goal?.auto_close_on_completion,
      notes: goal?.notes,
      createdAt: goal?.created_at,
      updatedAt: goal?.updated_at
    }));
  },

  // Get single goal by ID
  async getGoalById(goalId) {
    const { data, error } = await supabase?.from('financial_goals')?.select('*')?.eq('id', goalId)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      goalName: data?.goal_name,
      goalType: data?.goal_type,
      targetAmount: parseFloat(data?.target_amount),
      currentAmount: parseFloat(data?.current_amount),
      deadline: data?.deadline,
      status: data?.status,
      linkedAccountId: data?.linked_account_id,
      autoCloseOnCompletion: data?.auto_close_on_completion,
      notes: data?.notes,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Get goal statistics
  async getGoalStatistics(userId) {
    const { data, error } = await supabase?.rpc('get_goal_statistics', {
        p_user_id: userId
      });

    if (error) throw error;

    return data?.[0] ? {
      totalGoals: data?.[0]?.total_goals,
      activeGoals: data?.[0]?.active_goals,
      completedGoals: data?.[0]?.completed_goals,
      pausedGoals: data?.[0]?.paused_goals,
      overallProgress: parseFloat(data?.[0]?.overall_progress)
    } : {
      totalGoals: 0,
      activeGoals: 0,
      completedGoals: 0,
      pausedGoals: 0,
      overallProgress: 0
    };
  },

  // Create a new goal
  async createGoal(goalData) {
    const { data, error } = await supabase?.from('financial_goals')?.insert({
        user_id: goalData?.userId,
        goal_name: goalData?.goalName,
        goal_type: goalData?.goalType,
        target_amount: goalData?.targetAmount,
        current_amount: goalData?.currentAmount || 0,
        deadline: goalData?.deadline,
        linked_account_id: goalData?.linkedAccountId,
        auto_close_on_completion: goalData?.autoCloseOnCompletion || false,
        notes: goalData?.notes
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      goalName: data?.goal_name,
      goalType: data?.goal_type,
      targetAmount: parseFloat(data?.target_amount),
      currentAmount: parseFloat(data?.current_amount),
      deadline: data?.deadline,
      status: data?.status,
      linkedAccountId: data?.linked_account_id,
      autoCloseOnCompletion: data?.auto_close_on_completion,
      notes: data?.notes,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Update a goal
  async updateGoal(goalId, updates) {
    const updateData = {};
    if (updates?.goalName !== undefined) updateData.goal_name = updates?.goalName;
    if (updates?.targetAmount !== undefined) updateData.target_amount = updates?.targetAmount;
    if (updates?.deadline !== undefined) updateData.deadline = updates?.deadline;
    if (updates?.status !== undefined) updateData.status = updates?.status;
    if (updates?.linkedAccountId !== undefined) updateData.linked_account_id = updates?.linkedAccountId;
    if (updates?.autoCloseOnCompletion !== undefined) updateData.auto_close_on_completion = updates?.autoCloseOnCompletion;
    if (updates?.notes !== undefined) updateData.notes = updates?.notes;

    const { data, error } = await supabase?.from('financial_goals')?.update(updateData)?.eq('id', goalId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      goalName: data?.goal_name,
      goalType: data?.goal_type,
      targetAmount: parseFloat(data?.target_amount),
      currentAmount: parseFloat(data?.current_amount),
      deadline: data?.deadline,
      status: data?.status,
      linkedAccountId: data?.linked_account_id,
      autoCloseOnCompletion: data?.auto_close_on_completion,
      notes: data?.notes,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Delete a goal
  async deleteGoal(goalId) {
    const { error } = await supabase?.from('financial_goals')?.delete()?.eq('id', goalId);

    if (error) throw error;
  },

  // Get milestones for a goal
  async getMilestones(goalId) {
    const { data, error } = await supabase?.from('goal_milestones')?.select('*')?.eq('goal_id', goalId)?.order('milestone_value', { ascending: true });

    if (error) throw error;

    return data?.map(milestone => ({
      id: milestone?.id,
      goalId: milestone?.goal_id,
      milestoneName: milestone?.milestone_name,
      milestoneType: milestone?.milestone_type,
      milestoneValue: parseFloat(milestone?.milestone_value),
      achieved: milestone?.achieved,
      achievedAt: milestone?.achieved_at,
      rewardMessage: milestone?.reward_message,
      createdAt: milestone?.created_at
    }));
  },

  // Create milestone
  async createMilestone(milestoneData) {
    const { data, error } = await supabase?.from('goal_milestones')?.insert({
        goal_id: milestoneData?.goalId,
        milestone_name: milestoneData?.milestoneName,
        milestone_type: milestoneData?.milestoneType,
        milestone_value: milestoneData?.milestoneValue,
        reward_message: milestoneData?.rewardMessage
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      goalId: data?.goal_id,
      milestoneName: data?.milestone_name,
      milestoneType: data?.milestone_type,
      milestoneValue: parseFloat(data?.milestone_value),
      achieved: data?.achieved,
      achievedAt: data?.achieved_at,
      rewardMessage: data?.reward_message,
      createdAt: data?.created_at
    };
  },

  // Get contributions for a goal (includes automatic transaction-based contributions)
  async getContributions(goalId) {
    const { data, error } = await supabase?.from('goal_contributions')
      ?.select(`
        *,
        transactions (
          id,
          description,
          date,
          type
        )
      `)
      ?.eq('goal_id', goalId)
      ?.order('contribution_date', { ascending: false });

    if (error) throw error;

    return data?.map(contribution => ({
      id: contribution?.id,
      goalId: contribution?.goal_id,
      amount: parseFloat(contribution?.amount),
      contributionDate: contribution?.contribution_date,
      notes: contribution?.notes,
      transactionId: contribution?.transaction_id,
      isAutomatic: contribution?.is_automatic,
      transaction: contribution?.transactions ? {
        id: contribution?.transactions?.id,
        description: contribution?.transactions?.description,
        date: contribution?.transactions?.date,
        type: contribution?.transactions?.type
      } : null,
      createdAt: contribution?.created_at
    }));
  },

  // Add contribution
  async addContribution(contributionData) {
    const { data, error } = await supabase?.from('goal_contributions')?.insert({
        goal_id: contributionData?.goalId,
        amount: contributionData?.amount,
        contribution_date: contributionData?.contributionDate,
        notes: contributionData?.notes
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      goalId: data?.goal_id,
      amount: parseFloat(data?.amount),
      contributionDate: data?.contribution_date,
      notes: data?.notes,
      createdAt: data?.created_at
    };
  },

  // Calculate projected completion date
  async getProjectedCompletion(goalId) {
    const { data, error } = await supabase?.rpc('calculate_projected_completion', {
        p_goal_id: goalId
      });

    if (error) throw error;

    return data;
  },

  // Check goal completion and auto-close if enabled
  async checkGoalCompletion(goalId) {
    const { error } = await supabase?.rpc('check_goal_completion', {
        p_goal_id: goalId
      });

    if (error) throw error;
  },

  // Get goal with linked account details
  async getGoalWithAccount(goalId) {
    const { data, error } = await supabase
      ?.from('financial_goals')
      ?.select(`
        *,
        linked_account:accounts!linked_account_id(
          id,
          name,
          type,
          account_group,
          balance,
          currency
        )
      `)
      ?.eq('id', goalId)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      goalName: data?.goal_name,
      goalType: data?.goal_type,
      targetAmount: parseFloat(data?.target_amount),
      currentAmount: parseFloat(data?.current_amount),
      deadline: data?.deadline,
      status: data?.status,
      linkedAccountId: data?.linked_account_id,
      autoCloseOnCompletion: data?.auto_close_on_completion,
      notes: data?.notes,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      linkedAccount: data?.linked_account ? {
        id: data?.linked_account?.id,
        name: data?.linked_account?.name,
        type: data?.linked_account?.type,
        group: data?.linked_account?.account_group,
        balance: parseFloat(data?.linked_account?.balance),
        currency: data?.linked_account?.currency
      } : null
    };
  }
};

export default goalService;